/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, connect } from 'react-redux';
import { Field, reduxForm, SubmissionError, change, formValueSelector } from 'redux-form';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import waiting from '../assets/images/waiting.gif';
import { request, redirectToPage, defaultHeaders } from '../services/utilities';
import { API_URI, departmentAPI, rolesAPI } from '../services/constants';
import { notifySuccess } from '../services/notify';
import { loginUser } from '../actions/user';
import SSRStorage from '../services/storage';
import { FULLSCREEN_COOKIE, MODE_COOKIE, TOKEN_COOKIE, BROWSER_SESSION, isTestMode } from '../services/constants';
import { APP_NAME } from '../services/constants';
import { loadRoles } from '../actions/role';
import { loadDepartments } from '../actions/department';
import { loadSpecializations } from '../actions/settings';
import { setConnection } from '../actions/general';
import { initSocket, subscribeIO } from '../services/socket';
import RenderLoginTextInput from './LoginTextInput';
import SearchableSelect from './SearchableSelect';

const storage = new SSRStorage();

const axiosFetch = (url, jwt) =>
	axios.get(url, {
		headers: !jwt ? defaultHeaders : { ...defaultHeaders, Authorization: jwt },
	});

const validate = values => {
	const errors = {};
	if (!values.username) {
		errors.username = 'enter username';
	}
	if (!values.password) {
		errors.password = 'enter password';
	}
	return errors;
};

const Login = ({ location, history, error, handleSubmit, usernameValue }) => {
	const [state, setState] = useState({
		submitting: false,
		loaded: false,
	});
	const [isOnline, setIsOnline] = useState(false);
	const [testRoles, setTestRoles] = useState([]);
	const [testDepartments, setTestDepartments] = useState([]);
	const [testUnits, setTestUnits] = useState([]);
	const [selectedRoleId, setSelectedRoleId] = useState('');
	const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
	const [selectedUnitId, setSelectedUnitId] = useState('');
	const [otpSent, setOtpSent] = useState(false);
	const [emailFound, setEmailFound] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [otpCountdown, setOtpCountdown] = useState(120);
	const [resending, setResending] = useState(false);
	const countdownRef = useRef(null);
	const dispatch = useDispatch();

	const initSettings = useCallback(async () => {
		const fullscreen = await storage.getItem(FULLSCREEN_COOKIE);
		const theme_mode = await storage.getItem(MODE_COOKIE);
		const isLogin = location.pathname === '/';
		window.document.body.className = `menu-position-side menu-side-left${fullscreen || isLogin ? ' full-screen' : ''} with-content-panel${
			theme_mode ? ' color-scheme-dark' : ''
		}`;
	}, [location]);

	useEffect(() => {
		if (!state.loaded) {
			setState({ ...state, loaded: true });
			initSettings();
		}
	}, [initSettings, state]);

	const fetchTestRoles = useCallback(async () => {
		try {
			const rs = await request('auth/test/roles', 'GET');
			if (Array.isArray(rs)) {
				setTestRoles(rs);
			}
		} catch (e) {
			console.log('Failed to fetch test roles', e);
		}
	}, []);

	const fetchTestDepartments = useCallback(async roleId => {
		try {
			setTestDepartments([]);
			setSelectedDepartmentId('');
			setTestUnits([]);
			setSelectedUnitId('');
			if (!roleId) return;
			const rs = await request('auth/test/departments', 'POST', false, { roleId });
			if (Array.isArray(rs)) {
				setTestDepartments(rs);
			}
		} catch (e) {
			console.log('Failed to fetch test departments', e);
		}
	}, []);

	const fetchTestUnits = useCallback(async (roleId, departmentId) => {
		try {
			setTestUnits([]);
			setSelectedUnitId('');
			if (!roleId || !departmentId) return;
			const rs = await request('auth/test/units', 'POST', false, { roleId, departmentId });
			if (Array.isArray(rs)) {
				setTestUnits(rs);
			}
		} catch (e) {
			console.log('Failed to fetch test units', e);
		}
	}, []);

	useEffect(() => {
		if (isTestMode && state.loaded) {
			fetchTestRoles();
		}
	}, [state.loaded, fetchTestRoles]);

	const loadAdminProfile = useCallback(async username => {
		if (!username || !isTestMode) return;
		try {
			const rs = await request(`auth/${encodeURIComponent(username)}`, 'GET');
			if (rs && rs.role && rs.details) {
				const roleId = rs.role.id;
				setSelectedRoleId(roleId);
				const depts = await request('auth/test/departments', 'POST', false, { roleId });
				if (Array.isArray(depts)) {
					setTestDepartments(depts);
					if (rs.details.department) {
						const deptId = rs.details.department.id || rs.details.department;
						setSelectedDepartmentId(deptId);
						const units = await request('auth/test/units', 'POST', false, { roleId, departmentId: deptId });
						if (Array.isArray(units)) {
							setTestUnits(units);
							if (rs.details.unit) {
								setSelectedUnitId(rs.details.unit.id || rs.details.unit);
							}
						}
					}
				}
			}
		} catch (e) {
			console.log('Failed to load admin profile', e);
		}
	}, []);

	const usernameRef = useRef(usernameValue);
	useEffect(() => {
		if (!isTestMode) return;
		if (usernameValue && usernameValue !== usernameRef.current) {
			usernameRef.current = usernameValue;
			const timer = setTimeout(() => loadAdminProfile(usernameValue), 600);
			return () => clearTimeout(timer);
		}
	}, [usernameValue, loadAdminProfile]);

	const doLogin = async data => {
		try {
			setState({ ...state, submitting: true });
			const address = uuidv4();
			const detail = { ...data, address };
			const rs = await request('auth/login', 'POST', true, detail);

			if (rs && rs.token) {
				try {
					const jwt = `Bearer ${rs.token}`;
					let [rs_depts, rs_roles, rs_specializations] = await Promise.all([
						axiosFetch(`${API_URI}/${departmentAPI}`, jwt),
						axiosFetch(`${API_URI}/${rolesAPI}`, jwt),
						axiosFetch(`${API_URI}/specializations`, jwt),
					]);

					if (rs_depts && rs_depts.data) {
						dispatch(loadDepartments(rs_depts.data));
					}
					if (rs_roles && rs_roles.data) {
						dispatch(loadRoles(rs_roles.data));
					}
					if (rs_specializations && rs_specializations.data) {
						dispatch(loadSpecializations(rs_specializations.data));
					}

					dispatch(loginUser(rs));
					storage.setItem(TOKEN_COOKIE, rs);
					storage.setCookie(BROWSER_SESSION, '1', { path: '/' });

					notifySuccess('login successful!');

					initSocket();
					subscribeIO();

					dispatch(setConnection(true));

					if (rs.passwordChanged) {
						redirectToPage(rs.role, history, rs.permissions);
					} else {
						history.push('/change-password');
					}
				} catch (e) {
					console.log('ERROR 1 => ', rs);
					setState({ ...state, submitting: false });
					throw new SubmissionError({
						_error: 'could not login user',
					});
				}
			} else if (rs && rs.error) {
				console.log('ERROR 2 => ', rs);
				dispatch(change('login_user', 'bypass', 1));
				setState({ ...state, submitting: false });
				// throw prompt to logout other users
			} else {
				console.log('ERROR 3 => ', rs);
				setState({ ...state, submitting: false });
				throw new SubmissionError({
					_error: 'could not login user',
				});
			}
		} catch (e) {
			console.log('ERROR 4 => ', e);
			setState({ ...state, submitting: false });
			throw new SubmissionError({
				_error: e.message || 'could not login user',
			});
		}
	};

	const doTestLogin = async data => {
		try {
			if (!selectedRoleId) {
				throw new SubmissionError({ _error: 'Please select a role' });
			}
			if (!selectedDepartmentId) {
				throw new SubmissionError({ _error: 'Please select a department' });
			}
			setState({ ...state, submitting: true });
			const payload = {
				username: data.username,
				password: data.password,
				roleId: +selectedRoleId,
				departmentId: +selectedDepartmentId,
				unitId: selectedUnitId ? +selectedUnitId : undefined,
			};
			const rs = await request('auth/test/login', 'POST', true, payload);

			if (rs && rs.token) {
				try {
					const jwt = `Bearer ${rs.token}`;
					let [rs_depts, rs_roles, rs_specializations] = await Promise.all([
						axiosFetch(`${API_URI}/${departmentAPI}`, jwt),
						axiosFetch(`${API_URI}/${rolesAPI}`, jwt),
						axiosFetch(`${API_URI}/specializations`, jwt),
					]);

					if (rs_depts && rs_depts.data) {
						dispatch(loadDepartments(rs_depts.data));
					}
					if (rs_roles && rs_roles.data) {
						dispatch(loadRoles(rs_roles.data));
					}
					if (rs_specializations && rs_specializations.data) {
						dispatch(loadSpecializations(rs_specializations.data));
					}

					dispatch(loginUser(rs));
					storage.setItem(TOKEN_COOKIE, rs);
					storage.setCookie(BROWSER_SESSION, '1', { path: '/' });

					notifySuccess('login successful!');

					initSocket();
					subscribeIO();

					dispatch(setConnection(true));

					if (rs.passwordChanged) {
						redirectToPage(rs.role, history, rs.permissions);
					} else {
						history.push('/change-password');
					}
				} catch (e) {
					console.log('ERROR 1 => ', rs);
					setState({ ...state, submitting: false });
					throw new SubmissionError({ _error: 'could not login user' });
				}
			} else {
				setState({ ...state, submitting: false });
				throw new SubmissionError({ _error: rs?.message || 'could not login user' });
			}
		} catch (e) {
			console.log('ERROR 4 => ', e);
			setState({ ...state, submitting: false });
			throw new SubmissionError({
				_error: e.message || 'could not login user',
			});
		}
	};

	const doLoginOnline = async data => {
		try {
			setState({ ...state, submitting: true });
			const detail = { ...data, email };
			const rs = await request('auth/login-online', 'POST', true, detail);

			if (rs && rs.otpSent) {
				setOtpSent(true);
				setPassword(data.password);
				setState({ ...state, submitting: false });
				notifySuccess('Enter OTP!');
			} else if (rs && rs.error) {
				console.log('ERROR 2 => ', rs);
				dispatch(change('login_user', 'bypass', 1));
				setState({ ...state, submitting: false });
				// throw prompt to logout other users
			} else {
				console.log('ERROR 3 => ', rs);
				setState({ ...state, submitting: false });
				throw new SubmissionError({
					_error: 'could not login user',
				});
			}
		} catch (e) {
			console.log('ERROR 4 => ', e);
			setState({ ...state, submitting: false });
			throw new SubmissionError({
				_error: e.message || 'could not login user',
			});
		}
	};

	const doCheckEmail = async data => {
		try {
			if (!data.email || validateEmail(data.email)) {
				throw new SubmissionError({
					_error: 'Not a valid email address',
				});
			}

			setState({ ...state, submitting: true });
			const detail = { ...data };
			const rs = await request('auth/check-user-exist', 'POST', true, detail);

			if (rs && rs.userExists) {
				setEmail(data.email);
				setEmailFound(true);
				setState({ ...state, submitting: false });
				notifySuccess('Enter your password!');
			} else if (rs && rs.error) {
				console.log('ERROR 2 => ', rs);
				dispatch(change('login_user', 'bypass', 1));
				setState({ ...state, submitting: false });
				// throw prompt to logout other users
			} else {
				console.log('ERROR 3 => ', rs);
				setState({ ...state, submitting: false });
				throw new SubmissionError({
					_error: 'could not login user',
				});
			}
		} catch (e) {
			console.log('ERROR 4 => ', e);
			setState({ ...state, submitting: false });
			throw new SubmissionError({
				_error: e.message || 'could not login user',
			});
		}
	};

	const verifyOtp = async data => {
		try {
			if (!data.otp || data.otp?.length < 4) {
				throw new SubmissionError({
					_error: 'Not a valid OTP',
				});
			}
			setState({ ...state, submitting: true });
			const address = uuidv4();
			const detail = { ...data, email, password, address };
			const rs = await request('auth/verify-otp-online', 'POST', true, detail);

			if (rs && rs.token) {
				try {
					const jwt = `Bearer ${rs.token}`;
					let [rs_depts, rs_roles, rs_specializations] = await Promise.all([
						axiosFetch(`${API_URI}/${departmentAPI}`, jwt),
						axiosFetch(`${API_URI}/${rolesAPI}`, jwt),
						axiosFetch(`${API_URI}/specializations`, jwt),
					]);

					if (rs_depts && rs_depts.data) {
						dispatch(loadDepartments(rs_depts.data));
					}
					if (rs_roles && rs_roles.data) {
						dispatch(loadRoles(rs_roles.data));
					}
					if (rs_specializations && rs_specializations.data) {
						dispatch(loadSpecializations(rs_specializations.data));
					}

					dispatch(loginUser(rs));
					storage.setItem(TOKEN_COOKIE, rs);
					storage.setCookie(BROWSER_SESSION, '1', { path: '/' });

					notifySuccess('login successful!');

					initSocket();
					subscribeIO();

					dispatch(setConnection(true));

					if (rs.passwordChanged) {
						redirectToPage(rs.role, history, rs.permissions);
					} else {
						history.push('/change-password');
					}
				} catch (e) {
					console.log('ERROR 1 => ', rs);
					setState({ ...state, submitting: false });
					throw new SubmissionError({
						_error: 'could not login user',
					});
				}
			} else if (rs && rs.error) {
				console.log('ERROR 2 => ', rs);
				dispatch(change('login_user', 'bypass', 1));
				setState({ ...state, submitting: false });
				// throw prompt to logout other users
			} else {
				console.log('ERROR 3 => ', rs);
				setState({ ...state, submitting: false });
				throw new SubmissionError({
					_error: 'could not login user',
				});
			}
		} catch (e) {
			console.log('ERROR 4 => ', e);
			setState({ ...state, submitting: false });
			throw new SubmissionError({
				_error: e.message || 'could not login user',
			});
		}
	};

	const { submitting } = state;

	const query = location.search.replace('?', '');
	const sessions = query.split('=');
	const session = sessions.length > 0 ? sessions[0] : '';

	useEffect(() => {
		const hostname = window.location.hostname;
		console.log('HOST NAME ', hostname);
		if (hostname.endsWith('.dedahospital.com') && !hostname.startsWith('dev')) {
			setIsOnline(true);
			console.log('✅ Matches *(!dev-*).dedahospital.com');
		} else {
			setIsOnline(false);
			console.log('❌ Does not match');
		}
	}, []);

	useEffect(() => {
		if (otpSent) {
			setOtpCountdown(300);
			countdownRef.current = setInterval(() => {
				setOtpCountdown(prev => {
					if (prev <= 1) {
						clearInterval(countdownRef.current);
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => {
			if (countdownRef.current) clearInterval(countdownRef.current);
		};
	}, [otpSent]);

	const resendOtp = async () => {
		try {
			setResending(true);
			const rs = await request('auth/login-online', 'POST', true, { email, password });
			if (rs && rs.otpSent) {
				setOtpCountdown(300);
				if (countdownRef.current) clearInterval(countdownRef.current);
				countdownRef.current = setInterval(() => {
					setOtpCountdown(prev => {
						if (prev <= 1) {
							clearInterval(countdownRef.current);
							return 0;
						}
						return prev - 1;
					});
				}, 1000);
				notifySuccess('New OTP sent!');
			} else {
				throw new Error(rs?.message || 'Could not resend OTP');
			}
		} catch (e) {
			console.log('Resend OTP error => ', e);
		} finally {
			setResending(false);
		}
	};

	const formatCountdown = seconds => {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m}:${s < 10 ? '0' : ''}${s}`;
	};

	const OTP_TOTAL = 300;
	const otpProgress = otpCountdown / OTP_TOTAL;
	const circleRadius = 36;
	const circumference = 2 * Math.PI * circleRadius;
	const strokeDashoffset = circumference * (1 - otpProgress);

	const validateEmail = value => (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? 'Invalid email address' : undefined);

	return (
		<section className="fxt-animation template">
			<div className="bg-overlay">
				<div className="fxt-content">
					<div className="logo-header">
						<a className="fxt-logo">
							<img src={require('../assets/images/fmc_presentation.png')} alt="logo" />
						</a>
						<div style={{ color: '#fff', marginTop: '15px', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '2px' }}>
							{APP_NAME}
						</div>
					</div>
					{isOnline ? (
						<div className="fxt-form">
							{otpSent ? (
								<form onSubmit={handleSubmit(verifyOtp)}>
									{session !== '' && <div className="alert alert-danger" dangerouslySetInnerHTML={{ __html: `Session Expired!` }} />}
									{error && (
										<div
											className="alert alert-danger"
											dangerouslySetInnerHTML={{
												__html: `<strong>Error!</strong> ${error}`,
											}}
										/>
									)}

									{/* OTP Countdown */}
									<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
										<div style={{ position: 'relative', width: '90px', height: '90px' }}>
											<svg width="90" height="90" style={{ transform: 'rotate(-90deg)' }}>
												<circle cx="45" cy="45" r={circleRadius} fill="none" stroke="#e9ecef" strokeWidth="6" />
												<circle
													cx="45"
													cy="45"
													r={circleRadius}
													fill="none"
													stroke={otpCountdown > 60 ? '#4e73df' : otpCountdown > 0 ? '#fd7e14' : '#dc3545'}
													strokeWidth="6"
													strokeLinecap="round"
													strokeDasharray={circumference}
													strokeDashoffset={strokeDashoffset}
													style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s ease' }}
												/>
											</svg>
											<div
												style={{
													position: 'absolute',
													inset: 0,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontWeight: 700,
													fontSize: '18px',
													color: otpCountdown > 60 ? '#4e73df' : otpCountdown > 0 ? '#fd7e14' : '#dc3545',
												}}
											>
												{formatCountdown(otpCountdown)}
											</div>
										</div>
										<div style={{ marginTop: '8px', fontSize: '13px', color: '#6c757d', textAlign: 'center' }}>
											{otpCountdown > 0 ? 'OTP expires in ' + formatCountdown(otpCountdown) : 'OTP expired — request a new one'}
										</div>
										{/* Progress bar */}
										<div
											style={{
												width: '100%',
												height: '4px',
												background: '#e9ecef',
												borderRadius: '2px',
												marginTop: '10px',
												overflow: 'hidden',
											}}
										>
											<div
												style={{
													width: `${otpProgress * 100}%`,
													height: '100%',
													background: otpCountdown > 60 ? '#4e73df' : otpCountdown > 0 ? '#fd7e14' : '#dc3545',
													borderRadius: '2px',
													transition: 'width 1s linear, background 0.5s ease',
												}}
											/>
										</div>
									</div>

									<input type="hidden" name="email" autoComplete="username" value={email} readOnly />
									<input type="hidden" name="password" autoComplete="current-password" value={password} readOnly />
									<Field
										id="otp"
										name="otp"
										component={RenderLoginTextInput}
										type="text"
										placeholder="Enter OTP"
										className="form-control"
										icon="os-icon-user-male-circle"
										label="OTP"
										autoComplete="one-time-code"
									/>
									<div className="form-group mt-2">
										<div className="fxt-transformY-50 fxt-transition-delay-9">
											<button className="fxt-btn-fill" disabled={submitting} type="submit">
												{submitting ? <img src={waiting} alt="submitting" /> : 'Verify OTP'}
											</button>
										</div>
									</div>
									<div style={{ textAlign: 'center', marginTop: '12px' }}>
										<button
											type="button"
											disabled={otpCountdown > 0 || resending}
											onClick={resendOtp}
											style={{
												background: 'none',
												border: 'none',
												cursor: otpCountdown > 0 ? 'not-allowed' : 'pointer',
												color: otpCountdown > 0 ? '#adb5bd' : '#4e73df',
												fontWeight: 600,
												fontSize: '14px',
												textDecoration: otpCountdown > 0 ? 'none' : 'underline',
											}}
										>
											{resending ? 'Sending...' : 'Resend OTP'}
										</button>
									</div>
								</form>
							) : emailFound ? (
								<form onSubmit={handleSubmit(doLoginOnline)}>
									{session !== '' && <div className="alert alert-danger" dangerouslySetInnerHTML={{ __html: `Session Expired!` }} />}
									{error && (
										<div
											className="alert alert-danger"
											dangerouslySetInnerHTML={{
												__html: `<strong>Error!</strong> ${error}`,
											}}
										/>
									)}
									<input type="hidden" name="email" autoComplete="username" value={email} readOnly />
									<Field
										name="password"
										component={RenderLoginTextInput}
										type="password"
										placeholder="Enter your password"
										className="form-control"
										icon="os-icon-eye"
										label="Password"
										autoComplete="current-password"
									/>
									<div className="form-group mt-2">
										<div className="fxt-transformY-50 fxt-transition-delay-9">
											<button className="fxt-btn-fill" disabled={submitting} type="submit">
												{submitting ? <img src={waiting} alt="submitting" /> : 'Log me in'}
											</button>
										</div>
									</div>
								</form>
							) : (
								<form onSubmit={handleSubmit(doCheckEmail)}>
									{session !== '' && <div className="alert alert-danger" dangerouslySetInnerHTML={{ __html: `Session Expired!` }} />}
									{error && (
										<div
											className="alert alert-danger"
											dangerouslySetInnerHTML={{
												__html: `<strong>Error!</strong> ${error}`,
											}}
										/>
									)}
									<Field
										id="email"
										name="email"
										component={RenderLoginTextInput}
										type="email"
										placeholder="Enter your email"
										className="form-control"
										icon="os-icon-email-2-at2"
										label="Email"
										autoComplete="username"
									/>
									<div className="form-group mt-2">
										<div className="fxt-transformY-50 fxt-transition-delay-9">
											<button className="fxt-btn-fill" disabled={submitting} type="submit">
												{submitting ? <img src={waiting} alt="submitting" /> : 'Continue'}
											</button>
										</div>
									</div>
								</form>
							)}
						</div>
					) : (
						<div className="fxt-form">
							<form onSubmit={handleSubmit(isTestMode ? doTestLogin : doLogin)} autoComplete="off">
								{session !== '' && <div className="alert alert-danger" dangerouslySetInnerHTML={{ __html: `Session Expired!` }} />}
								{error && (
									<div
										className="alert alert-danger"
										dangerouslySetInnerHTML={{
											__html: `<strong>Error!</strong> ${error}`,
										}}
									/>
								)}
								{isTestMode && (
									<div style={{ marginBottom: '12px', textAlign: 'center' }}>
										<span
											style={{
												background: '#ffc107',
												color: '#000',
												padding: '2px 10px',
												borderRadius: '4px',
												fontSize: '12px',
												fontWeight: 600,
											}}
										>
											TEST MODE
										</span>
									</div>
								)}
								<Field
									id="username"
									name="username"
									component={RenderLoginTextInput}
									type="text"
									placeholder="Enter your username"
									className="form-control"
									icon="os-icon-user-male-circle"
									label="Username"
								/>
								<Field
									name="password"
									component={RenderLoginTextInput}
									type="password"
									placeholder="Enter your password"
									className="form-control"
									icon="os-icon-eye"
									label="Password"
								/>
								{isTestMode && (
									<>
										<div className="form-group" style={{ marginBottom: '15px' }}>
											<SearchableSelect
												options={testRoles}
												value={selectedRoleId}
												placeholder="-- Select Role --"
												onChange={val => {
													setSelectedRoleId(val);
													fetchTestDepartments(val);
												}}
											/>
										</div>
										<div className="form-group" style={{ marginBottom: '15px' }}>
											<SearchableSelect
												options={testDepartments}
												value={selectedDepartmentId}
												placeholder="-- Select Department --"
												disabled={!selectedRoleId}
												onChange={val => {
													setSelectedDepartmentId(val);
													fetchTestUnits(selectedRoleId, val);
												}}
											/>
										</div>
										<div className="form-group" style={{ marginBottom: '15px' }}>
											<SearchableSelect
												options={testUnits}
												value={selectedUnitId}
												placeholder="-- Select Unit --"
												disabled={!selectedDepartmentId}
												onChange={val => setSelectedUnitId(val)}
											/>
										</div>
									</>
								)}
								<div className="form-group mt-2">
									<div className="fxt-transformY-50 fxt-transition-delay-9">
										<button className="fxt-btn-fill" disabled={submitting} type="submit">
											{submitting ? <img src={waiting} alt="submitting" /> : 'Log me in'}
										</button>
									</div>
								</div>
							</form>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

const selector = formValueSelector('login_user');

const mapStateToProps = state => {
	return {
		initialValues: {
			bypass: 0,
		},
		usernameValue: selector(state, 'username'),
	};
};

export default connect(mapStateToProps)(reduxForm({ form: 'login_user', validate })(Login));
